import { useTranslation } from 'react-i18next';

import { cn } from '~/utils/cn';

/** Competition band, lowercased for style-map lookups and i18n keys. */
export type CompetitionLevel = 'low' | 'medium' | 'high';

/** Keyword Planner competition band + index (0–100). `null` when Google
 *  returns neither — render "Not available", never a `Low (0)` guess. */
export type CompetitionBand = { level: CompetitionLevel; index: number } | null;

/** Dot colour per competition band — reuses the app's existing semantic
 *  tone tokens rather than introducing new ones. */
const COMPETITION_DOT_COLOR: Record<CompetitionLevel, string> = {
  low: 'bg-success',
  medium: 'bg-warning',
  high: 'bg-destructive',
};

/**
 * Competition cell: a coloured dot, the band label, and its numeric index in
 * parens (e.g. "● Medium (52)"). Renders a muted "Not available" when Google
 * returns no competition data. Shared by the Product Terms table and the
 * term-products drawer's header strip — both read the same wire shape.
 */
export function CompetitionCell({
  competition,
  testId = 'competition-cell',
}: {
  competition: CompetitionBand;
  /** `data-testid` root (default `competition-cell`); derives `-empty`, `-level`, `-index`. */
  testId?: string;
}) {
  const { t } = useTranslation('common');

  if (!competition) {
    return (
      <span className="text-muted-foreground text-sm" data-testid={`${testId}-empty`}>
        {t('competitionCell.notAvailable')}
      </span>
    );
  }

  return (
    <span
      className="flex items-center gap-2 text-sm"
      data-testid={testId}
      data-competition-level={competition.level}
    >
      <span
        aria-hidden="true"
        className={cn('h-2 w-2 shrink-0 rounded-full', COMPETITION_DOT_COLOR[competition.level])}
      />
      <span className="text-foreground font-semibold" data-testid={`${testId}-level`}>
        {t(`competitionCell.levels.${competition.level}`)}
      </span>
      <span className="text-muted-foreground" data-testid={`${testId}-index`}>
        ({competition.index})
      </span>
    </span>
  );
}
